/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useToasts } from "react-toast-notifications";
import { LocationsContext } from ".";
import fetchHelper from "../../services/fetchHelper";
import dayjs from "dayjs";
import produce from "immer";
import { useForm, Controller } from "react-hook-form";
import "./site-information.scss";
import { LayoutContext } from "../../components/layout";
import { formatFileName } from "../../utils/helpers";
import { featureCheck as featureFlagCheckFromJson } from "../../utils/feature";
import useFeatureFlag from "../../utils/featureFlag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import MimeTypes from "../../constants/MimeTypes";
import startCase from "lodash/startCase";
import { EmrIntegrations } from "../../constants/LocationMetasConstants";
import { LocationIntegrationCheckbox } from "../../components/LocationIntegrations/LocationInformationHandler";
import { RLSIntegrationSettings } from "../../components/LocationIntegrations/RLSIntegrationSettings";
import { FHIRIntegrationSettings } from "../../components/LocationIntegrations/FHIRIntegrationSettings";
import { CPT_CODES_ENABLED } from "../../constants/AppConfigConstants";

const CPT_PRACTICES_UPDATE_ENABLED = "practice-updates-enabled";
const CPT_PRACTICES_UPDATE_ENABLED_FOR_PRACTICES = "practice-updates-enabled-for-practices";
const CPT_PRACTICES_MGMT_ENABLED = "practice-mgmt-changes-enabled";
const CPT_PRACTICES_MGMT_ENABLED_FOR_PRACTICES = "practice-mgmt-changes-enabled-for-practices";

const locationStatusOptions = [
  { key: "Onboarding", value: "ONBOARDING" },
  { key: "Active", value: "ACTIVE" },
  { key: "Offboarding", value: "OFFBOARDING" },
  { key: "Disabled", value: "DISABLED" },
];

const SiteInformation = () => {
  const { addToast } = useToasts();
  const { setCurrentLocation } = useContext(LayoutContext);
  const { location, information, setInformation, setLocation } = useContext(LocationsContext);
  const [data, setData] = useState(location || {});
  const [previewLogo, setPreviewLogo] = useState(location.logoUrl);
  const [logoErrors, setLogoErrors] = useState([]);
  const [pdfErrors, setPdfErrors] = useState([]);
  const [isValidEnrollmentUrl, setIsValidEnrollmentUrl] = useState(true);
  const [shouldResetEnrollmentUrl, setShouldResetEnrollmentUrl] = useState(false);
  const [previewPdf, setPreviewPdf] = useState();
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [billingModels, setBillingModels] = useState({ deviceTypes: [], billingModels: [] });
  const { handleSubmit, errors, clearErrors, control, reset } = useForm();
  const [pdfLastUpdateUser, setPdfLastUpdateUser] = useState("");
  const hiddenLogoInput = useRef(null);
  const hiddenPdfInput = useRef(null);
  const fileNamePdf = useRef("");
  const formData = useRef(new FormData());
  const initialPreviewPdf = useRef(null);
  const siteInformationForm = useRef(null);

  const supportedImageFormats = ["image/png", "image/jpeg", "image/jpg", "image/bmp"];
  const supportedPdfFormats = [MimeTypes.PDF];
  const [locationMetas, setLocationMetas] = useState([]);
  const { getFlag } = useFeatureFlag();

  useEffect(() => {
    setData(location);
  }, [location]);

  useEffect(() => {
    if (information.cancel) {
      setDefaultLocationInformation(data);
      fileNamePdf.current = "";
      setData(location);

      if (showBillingModelsDropdowns()) {
        // reset to initial values billing models
        const resetValuesBillingModels = { locationStatus: location?.locationStatus?.value };
        location.locationBillingModels.map(({ deviceType }) => {
          const selectValue = getBillingModelValue(location.locationBillingModels, deviceType.externalId);
          const billingModelsId = `loc-${deviceType.value.toLowerCase()}-bill-dropdown`;
          resetValuesBillingModels[billingModelsId] = selectValue;
        });

        reset(resetValuesBillingModels);
      }

      setPreviewLogo(location.logoUrl);
      if (!location.escalationCriteriaPdfFileName) {
        setPreviewPdf(null);
      } else {
        setPreviewPdf(initialPreviewPdf.current);
      }
      setLogoErrors([]);
      setPdfErrors([]);
      formData.current.delete("file");
      setShouldResetEnrollmentUrl(true);
    }
  }, [information.cancel]);

  useEffect(() => {
    if (shouldResetEnrollmentUrl) {
      onEnrollmentUrlChange();
    }
  }, [data.enrollmentUrl, shouldResetEnrollmentUrl]);

  useEffect(() => {
    setInformation({ disabled: !isValidEnrollmentUrl || !!pdfErrors.length || !!logoErrors.length });
  }, [isValidEnrollmentUrl, pdfErrors, logoErrors]);

  useEffect(() => {
    async function fetchPdf() {
      await getPdfPreview();
    }

    if (data.id && featureFlagCheckFromJson("escalationCriteria.enable")) {
      fetchPdf();
    }
  }, [data.id]);

  useEffect(() => {
    setDefaultLocationInformation(data);
    fetchBillingModels();
  }, []);

  const fetchBillingModels = async () => {
    try {
      const data = await fetchHelper.get({ url: "/patientmanagement/rest/billing/billingmodels" });
      setBillingModels(data);
    } catch (err) {
      addToast("There is an error while getting the Billing Models", {
        appearance: "error",
        autoDismiss: false,
      });
    }
  };

  const setDefaultLocationInformation = (data) => {
    setLocationMetas(data.locationMetas || []);
  };

  useEffect(() => {
    async function getUserById() {
      await fetchUserById(data.escalationCriteriaLastUpdateUser);
    }

    getUserById();
  }, [location.escalationCriteriaLastUpdateUser]);

  const save = async () => {
    if (data.patientManagementSetupDetails != null) {
      data.patientManagementSetup = JSON.stringify(data.patientManagementSetupDetails);
    }
    if (information.loading) return false;
    try {
      const hasLogoToUpload = formData.current.has("file");
      setInformation({ loading: true });
      const formResponse = await fetchHelper.put(`/patientmanagement/rest/location/${data.id}`, {
        ...data,
        locationMetas,
      });
      const logoResponse =
        hasLogoToUpload &&
        (await fetchHelper.put(`/patientmanagement/rest/location/${data.id}/logo`, formData.current));
      setInformation({ loading: false, openForm: false });
      setLocation(formResponse);
      setCurrentLocation(formResponse);
      setData(formResponse);
      setDefaultLocationInformation(formResponse);
      await Promise.all([fetchUserById(formResponse.escalationCriteriaLastUpdateUser), getPdfPreview()]);
      hasLogoToUpload && setPreviewLogo(logoResponse);
    } catch ({ errors }) {
      setInformation({ loading: false });
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  useEffect(() => {
    if (information.save) {
      siteInformationForm.current.dispatchEvent(new Event("submit"));
    }
  }, [information.save]);

  const onChange = (e) => {
    const { type, checked, value, dataset, name } = e.target;
    clearErrors(dataset.key);
    const key = dataset.key || name;
    const ret = type === "checkbox" ? checked : value.trim();
    setData((prev) =>
      produce(prev, (draft) => {
        draft[key] = ret;
      })
    );
  };

  const onEnrollmentUrlChange = () => {
    const enrollmentUrl = data?.enrollmentUrl;
    if (enrollmentUrl) {
      const isValid = !!enrollmentUrl.match(/^https:\/\/hipaa\.jotform\.com\/(\d+)\/prefill\/[0-9a-f]+\/?$/i);
      setIsValidEnrollmentUrl(isValid);
    } else {
      setIsValidEnrollmentUrl(true);
    }
  };

  const onChangeLocationStatus = (e) => {
    const value = e.target.value;
    setData((prev) =>
      produce(prev, (draft) => {
        draft.locationStatus = { value };
      })
    );
  };

  function getBase64(file, type = "dataUrl") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      if (type === "buffer") {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsDataURL(file);
      }
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  const showLocationStatus = () => {
    const cptCodesForLocationStatus = getFlag(CPT_CODES_ENABLED);
    if (
      cptCodesForLocationStatus.enabled &&
      cptCodesForLocationStatus.attributes[CPT_PRACTICES_UPDATE_ENABLED]
    ) {
      const allPracticesForLocationStatus =
        cptCodesForLocationStatus.attributes[CPT_PRACTICES_UPDATE_ENABLED_FOR_PRACTICES] || [];
      return allPracticesForLocationStatus.length === 0 || allPracticesForLocationStatus.includes(data.id);
    }
    return false;
  };

  const onChangeLogo = async (e) => {
    formData.current.delete("file");
    const fileName = formatFileName(e.target.value);
    const uploadedFile = e.target?.files[0];
    const hasErrors = isValidLogo(uploadedFile);
    const logoBase64 = await getBase64(uploadedFile);
    setPreviewLogo(logoBase64);

    setData((prev) =>
      produce(prev, (draft) => {
        draft.logoUrl = fileName;
      })
    );

    if (uploadedFile && !hasErrors) {
      formData.current.append("file", uploadedFile, fileName);
    }
  };

  const onChangeLocationMeta = (_e, meta) => {
    setLocationMetas((prev) =>
      produce(prev, (draft) => {
        const ret = draft.find(({ metaKey }) => meta.metaKey == metaKey);
        if (ret) {
          ret.metaValue = meta.metaValue;
        } else {
          draft.push(meta);
        }
      })
    );
  };

  const onChangePdf = async (e) => {
    const uploadedFile = e.currentTarget.files[0];
    if (uploadedFile && !isValidPdf(uploadedFile)) {
      const [pdfBuffer, pdfBase64] = await Promise.all([
        getBase64(uploadedFile, "buffer"),
        getBase64(uploadedFile),
      ]);
      const blob = new Blob([new Uint8Array(pdfBuffer)], { type: MimeTypes.PDF });
      setPreviewPdf(URL.createObjectURL(blob));

      fileNamePdf.current = uploadedFile.name;

      setData((prev) =>
        produce(prev, (draft) => {
          draft.escalationCriteriaBase64 = pdfBase64.split(",")[1];
          draft.escalationCriteriaPdfFileName = uploadedFile.name;
        })
      );
    } else {
      fileNamePdf.current = "";
      setPreviewPdf(null);
      setData((prev) =>
        produce(prev, (draft) => {
          draft.escalationCriteriaPdfFileName = null;
        })
      );
    }
  };

  const getPdfPreview = async () => {
    const buffer = await fetchHelper.getFile({
      url: `/patientmanagement/rest/location/${data.id}/download/escalationCriteria`,
      headers: { "Content-Type": MimeTypes.PDF },
      arrayBuffer: true,
    });

    if (buffer.byteLength === 0) {
      setPreviewPdf(null);
    } else {
      const blob = new Blob([buffer], { type: MimeTypes.PDF });
      const blobURL = URL.createObjectURL(blob);
      setPreviewPdf(blobURL);
      initialPreviewPdf.current = blobURL;
    }
  };

  const fetchUserById = async (idLastUpdateUser) => {
    if (idLastUpdateUser) {
      const { name, family } = await fetchHelper.get({
        url: `/patientmanagement/rest/users/${idLastUpdateUser}/loadUser`,
      });

      setPdfLastUpdateUser(`${startCase(family)}, ${startCase(name)}`);
    }
  };

  const isValidPdf = (file) => {
    const errors = [];

    if (!supportedPdfFormats.includes(file.type)) errors.push("The file format is not supported.");
    if (file.size === 0) errors.push("The file is empty.");

    setPdfErrors(errors);
    return !!errors.length;
  };

  const isValidLogo = (file) => {
    const ONE_MB_IN_BYTES = 1048576;
    const errors = [];

    if (!supportedImageFormats.includes(file.type)) errors.push("The file format is not supported.");
    if (file.size > ONE_MB_IN_BYTES) errors.push("The file size is larger than the limit.");

    setPreviewLogo(null);
    setLogoErrors(errors);
    return !!errors.length;
  };

  const togglePdfModal = () => {
    setIsPdfModalOpen(!isPdfModalOpen);
  };

  const billingServiceOptions = [
    { key: "SOFTWARE", value: "SOFTWARE" },
    { key: "INTERNAL", value: "INTERNAL" },
    { key: "EXTERNAL", value: "EXTERNAL" },
    { key: "HYBRID", value: "HYBRID" },
    { key: "SPLIT_COMM", value: "SPLIT-Comm" },
  ];

  const getBillingModelValue = (locationBillingModels, deviceTypeDropdown) => {
    if (!locationBillingModels?.length) {
      return null;
    }
    const deviceFound = locationBillingModels.find(
      ({ deviceType }) => deviceType.externalId === deviceTypeDropdown
    );
    return deviceFound?.billingModel?.externalId;
  };

  const dropdownsBillingModel = (deviceType) => {
    const selectValue = getBillingModelValue(data.locationBillingModels, deviceType.externalId);
    const billingModelsId = `loc-${deviceType.value.toLowerCase()}-bill-dropdown`;
    return (
      <div className="col col20" key={deviceType.externalId}>
        <label>
          <span>
            {deviceType.value} <sub className="required-field">*</sub>
          </span>
          <Controller
            control={control}
            name={billingModelsId}
            defaultValue={selectValue || ""}
            rules={{
              required: "Required fields missing data. Please review and re-submit.",
            }}
            render={(field) => (
              <>
                <select
                  {...field}
                  data-key="locationBillingModels"
                  onChange={(e) => {
                    onChangeLocationBillingModels(e, deviceType.externalId);
                    field.onChange(e);
                  }}
                  disabled={!information.openForm}
                  id={billingModelsId}
                >
                  <option value="">Select {deviceType.value}...</option>
                  {billingModels.billingModels.map(({ value, externalId }, index) => (
                    <option key={index} value={externalId}>
                      {value}
                    </option>
                  ))}
                </select>
                <span className="error">{errors[billingModelsId] && errors[billingModelsId].message}</span>
              </>
            )}
          />
        </label>
      </div>
    );
  };

  const showBillingModelsDropdowns = () => {
    const cptCodesForBillingModels = getFlag(CPT_CODES_ENABLED);
    if (cptCodesForBillingModels.enabled && cptCodesForBillingModels.attributes[CPT_PRACTICES_MGMT_ENABLED]) {
      const allPractices =
        cptCodesForBillingModels.attributes[CPT_PRACTICES_MGMT_ENABLED_FOR_PRACTICES] || [];
      return allPractices.length === 0 || allPractices.includes(data.id);
    }
    return false;
  };

  const onChangeLocationBillingModels = (e, deviceTypeExternalId) => {
    const billingExternalId = e.target.value;
    const indexDeviceFound = data.locationBillingModels.findIndex(
      ({ deviceType }) => deviceType.externalId === deviceTypeExternalId
    );
    setData((prev) =>
      produce(prev, (draft) => {
        if (indexDeviceFound === -1) {
          draft.locationBillingModels.push({
            deviceType: {
              externalId: deviceTypeExternalId,
            },
            billingModel: {
              externalId: billingExternalId,
            },
          });
        } else {
          draft.locationBillingModels[indexDeviceFound].billingModel.externalId = billingExternalId;
        }
      })
    );
  };

  return (
    <form onSubmit={handleSubmit(save)} ref={siteInformationForm}>
      <div className="tabContent ptInformation site-information">
        <div className="content">
          <div className="row rowStretch">
            <div className="col col-m-100 col100 pr0">
              <div className="grpHdrPg">
                <h3>
                  Location Profile: <span></span>
                </h3>
              </div>

              <div className="grpHdr">
                <h3>Location Information</h3>
                {showLocationStatus() || showBillingModelsDropdowns() ? (
                  <span className="message danger-message">* Indicates Required Field</span>
                ) : null}
              </div>
              <div className="row rowStretch locInfo">
                <div className="col col33">
                  <label>
                    <span>Location Name</span>
                    <input
                      type="text"
                      placeholder="Enter Location Name"
                      value={data.name || ""}
                      onChange={onChange}
                      data-key="name"
                      disabled={!information.openForm && localStorage.getItem("rccmgmt") === "true"}
                      id="location-name-text"
                    />
                  </label>
                </div>
                <div className="col col33">
                  <label>
                    <span>Phone</span>
                    <input
                      type="text"
                      value={data.phoneNumber || ""}
                      onChange={onChange}
                      data-key="phoneNumber"
                      disabled={!information.openForm}
                      id="phone-text"
                    />
                  </label>
                </div>
                <div className="col col33">
                  <label>
                    <span>Ext.</span>
                    <input
                      type="text"
                      value={data.phoneNumberExt || ""}
                      onChange={onChange}
                      data-key="phoneNumberExt"
                      disabled={!information.openForm}
                      id="phone-ext-text"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Description</span>
                    <input
                      type="text"
                      value={data.description || ""}
                      onChange={onChange}
                      data-key="description"
                      disabled={!information.openForm}
                      id="loca-des-text"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Address Line 1</span>
                    <input
                      type="text"
                      value={data.addressLine1 || ""}
                      onChange={onChange}
                      data-key="addressLine1"
                      disabled={!information.openForm}
                      id="addr1-text"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Address Line 2</span>
                    <input
                      type="text"
                      value={data.addressLine2 || ""}
                      onChange={onChange}
                      data-key="addressLine2"
                      disabled={!information.openForm}
                      id="addr2-text"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch locInfo">
                <div className="col col33">
                  <label>
                    <span>City</span>
                    <input
                      type="text"
                      placeholder="Enter City"
                      value={data.city || ""}
                      onChange={onChange}
                      data-key="city"
                      disabled={!information.openForm}
                      id="city-text"
                    />
                  </label>
                </div>
                <div className="col col33">
                  <label>
                    <span>State</span>
                    <input
                      type="text"
                      value={data.state || ""}
                      onChange={onChange}
                      data-key="state"
                      disabled={!information.openForm}
                      id="state-text"
                    />
                  </label>
                </div>
                <div className="col col33">
                  <label>
                    <span>Zip</span>
                    <input
                      type="text"
                      value={data.zip || ""}
                      onChange={onChange}
                      data-key="zip"
                      disabled={!information.openForm}
                      id="zip-text"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col33">
                  <label>
                    <span>Logo</span>
                    <input
                      id="logo-upload"
                      type="file"
                      ref={hiddenLogoInput}
                      onChange={onChangeLogo}
                      disabled={!information.openForm}
                      hidden={true}
                      accept={supportedImageFormats}
                    />
                    <input
                      data-testid="location-logo"
                      type="text"
                      className="cursor-default"
                      value={formatFileName(data.logoUrl) || "No file selected"}
                      disabled={true}
                      id="logo-file"
                    />
                  </label>
                  <div className="logo-notes">
                    <span>Supported image formats include: png, jpeg, jpg, and bmp.</span>
                    <span>Max size: 1 MB.</span>
                  </div>
                </div>
                <div className="col col33 center-aligned">
                  <button
                    htmlFor="logo-upload"
                    className="btn btnTeal"
                    onClick={() => {
                      hiddenLogoInput.current.click();
                    }}
                    disabled={!information.openForm}
                    id="logo-img-btn"
                  >
                    Browse for an image
                  </button>
                </div>
              </div>
              {logoErrors.length > 0 && (
                <div className="row rowStretch">
                  <div className="col col33">
                    {logoErrors.map((error, index) => (
                      <span key={index} className="error">
                        {error}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {previewLogo && (
                <div className="row rowStretch">
                  <div className="col col33">
                    <img className="preview-logo" src={previewLogo} alt={data.name} />
                  </div>
                </div>
              )}

              {featureFlagCheckFromJson("escalationCriteria.enable") && (
                <>
                  <div className={`row rowStretch ${showBillingModelsDropdowns() ? "form-divider" : ""}`}>
                    <div className="col col33">
                      <label>
                        <span>Practice Escalation Criteria</span>
                        <input
                          id="location-pdf"
                          data-testid="location-pdf"
                          type="file"
                          ref={hiddenPdfInput}
                          onChange={onChangePdf}
                          onClick={(event) => {
                            event.currentTarget.value = null;
                          }}
                          disabled={!information.openForm}
                          hidden
                          accept={supportedPdfFormats}
                        />
                        <input
                          type="text"
                          className="cursor-default"
                          value={
                            fileNamePdf.current || data.escalationCriteriaPdfFileName || "No file selected"
                          }
                          disabled
                          id="pdf-file"
                        />
                      </label>
                      {previewPdf && (
                        <FontAwesomeIcon
                          id="pdf-external-link"
                          data-testid="pdf-external-link"
                          className="pdf-external-link"
                          icon={faExternalLinkAlt}
                          onClick={togglePdfModal}
                        />
                      )}
                      <div className="logo-notes">
                        <span>Must be a signed Geneva Escalation Criteria document.</span>
                        <span>Supported file formats include: pdf.</span>
                      </div>
                      {isPdfModalOpen && (
                        <div className="wrapper-pdf">
                          <FontAwesomeIcon
                            className="close-pdf-modal"
                            icon={faTimes}
                            onClick={togglePdfModal}
                            id="close-pdf-modal"
                          />
                          <div className="content-pdf">
                            {previewPdf && (
                              <Worker workerUrl="/ReactPdfWorker.min.js">
                                <Viewer fileUrl={previewPdf} />
                              </Worker>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col wrap-pdf-button">
                      <button
                        htmlFor="location-pdf"
                        className="btn btnTeal"
                        onClick={() => {
                          hiddenPdfInput.current.click();
                        }}
                        disabled={!information.openForm}
                        id="pdf-file-btn"
                      >
                        Browse for a PDF
                      </button>
                    </div>
                    {data.escalationCriteriaLastUpdateUser && pdfLastUpdateUser && (
                      <div className="col col33 wrap-pdf-button logo-notes">
                        <span>
                          {`Last uploaded by ${pdfLastUpdateUser} on
                  ${dayjs(data.escalationCriteriaLastUpdateDate).format("MM/DD/YY")} at
                  ${dayjs(data.escalationCriteriaLastUpdateDate).format("hh:mm:ss a")}`}
                        </span>
                      </div>
                    )}
                  </div>
                  {pdfErrors.length > 0 && (
                    <div className="row rowStretch">
                      <div className="col col33">
                        {pdfErrors.map((error, index) => (
                          <span key={index} className="error">
                            {error}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {data.id && showBillingModelsDropdowns() ? (
                <div className="row rowStretch locInfo">
                  {billingModels.deviceTypes.map((deviceType) => dropdownsBillingModel(deviceType))}
                </div>
              ) : null}
            </div>
            <div className="col col-m-100 col60">
              <div className="grpHdr">
                <h3>Location Codes</h3>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Biotronik</span>
                    <input
                      type="text"
                      value={data.codeBiotronik || ""}
                      onChange={onChange}
                      data-key="codeBiotronik"
                      disabled={!information.openForm}
                      id="biotronik-code-txt"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100 boston-scientific-col">
                  <label>
                    <span>Boston Scientific</span>
                    <input
                      type="text"
                      value={data.codeBostonsci || ""}
                      onChange={onChange}
                      data-key="codeBostonsci"
                      disabled={!information.openForm}
                      id="bostonsic-code-txt"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Medtronic</span>
                    <input
                      type="text"
                      value={data.codeMedtronic || ""}
                      onChange={onChange}
                      data-key="codeMedtronic"
                      disabled={!information.openForm}
                      id="medtronic-code-txt"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>St. Jude</span>
                    <input
                      type="text"
                      value={data.codeStjude || ""}
                      onChange={onChange}
                      data-key="codeStjude"
                      disabled={!information.openForm}
                      id="stjude-code-txt"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Biotel</span>
                    <input
                      type="text"
                      value={data.codeBiotel || ""}
                      onChange={onChange}
                      data-key="codeBiotel"
                      disabled={!information.openForm}
                      id="biotel-code-txt"
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Billing</span>
                    <select
                      value={data.techBillingModel || ""}
                      onChange={onChange}
                      data-key="techBillingModel"
                      disabled={!information.openForm}
                      id="billing-dropdown"
                    >
                      <option value="NONE">Select Billing...</option>
                      {billingServiceOptions.map(({ value, key }, index) => (
                        <option key={index} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col100">
                  <label>
                    <span>Service</span>
                    <select
                      value={data.locService || ""}
                      onChange={onChange}
                      data-key="locService"
                      disabled={!information.openForm}
                      id="loc-ser-dropdown"
                    >
                      <option value="">Service...</option>
                      {["YES", "NO"].map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              {showLocationStatus() ? (
                <div className="row rowStretch">
                  <div className="col col100">
                    <label>
                      <span>
                        Location Status <sub className="required-field">*</sub>
                      </span>
                      <Controller
                        control={control}
                        name="locationStatus"
                        defaultValue={data?.locationStatus?.value || ""}
                        rules={{
                          required: "Required fields missing data. Please review and re-submit.",
                        }}
                        render={(field) => (
                          <>
                            <select
                              {...field}
                              value={data?.locationStatus?.value || ""}
                              onChange={(e) => {
                                onChangeLocationStatus(e);
                                field.onChange(e);
                              }}
                              data-key="locationStatus"
                              disabled={!information.openForm}
                              id="loc-status-dropdown"
                              name="locationStatus"
                            >
                              <option value="">Location Status...</option>
                              {locationStatusOptions.map(({ value, key }, index) => (
                                <option key={index} value={key}>
                                  {value}
                                </option>
                              ))}
                            </select>
                            <span className="error">
                              {errors.locationStatus && errors.locationStatus.message}
                            </span>
                          </>
                        )}
                      />
                    </label>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="col col-m-100 col60">
              <div className="section-header">
                <h3>Integration Information</h3>
                <span className="message">(select all that apply)</span>
              </div>
              <div className="row rowStretch">
                <div className="col col100 integration-information" id="biotronik-code-txt">
                  {data.id &&
                    EmrIntegrations.map((item, i) => (
                      <LocationIntegrationCheckbox
                        key={i}
                        integration={item}
                        checked={data[item.id]}
                        onChange={onChange}
                      />
                    ))}
                </div>
              </div>
            </div>
            {data.rls && (
              <div className="col col-m-100 col60">
                <div className="section-header">
                  <h3>RLS Integration Settings</h3>
                </div>
                <RLSIntegrationSettings
                  locationMetas={locationMetas}
                  onChangeLocationMeta={onChangeLocationMeta}
                />
              </div>
            )}
            {data?.fhir && (
              <div className="col col-m-100 col60">
                <div className="section-header">
                  <h3>FHIR Patient Query Settings</h3>
                </div>
                <FHIRIntegrationSettings
                  locationMetas={locationMetas}
                  onChangeLocationMeta={onChangeLocationMeta}
                />
              </div>
            )}
            <div className="col col-m-100 col60">
              <div className="section-header">
                <h3>Enrollment URL</h3>
              </div>
              <div className="row rowStretch">
                <div className="col col100 enrollment-url">
                  <label>
                    <input
                      id="enrollment-url-input"
                      data-testid="enrollment-url-input"
                      type="url"
                      placeholder="e.g. https://hipaa.jotform.com/form/..."
                      data-key="enrollmentUrl"
                      onChange={onChange}
                      onBlur={onEnrollmentUrlChange}
                      disabled={!information.openForm}
                      value={data.enrollmentUrl || ""}
                    />
                  </label>
                </div>
              </div>

              {!isValidEnrollmentUrl && data.enrollmentUrl && (
                <div className="row rowStretch">
                  <div className="col col60">
                    <label className="error" data-testid="enrollment-url-error-message">
                      Please introduce a valid Jotform URL.
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SiteInformation;
